import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@mui/material/Alert/Alert';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import Stack from '@mui/material/Stack/Stack';
import Switch from '@mui/material/Switch/Switch';
import Typography from '@mui/material/Typography/Typography';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import Order from '@/shared/types/order/order.type';

type CancelOrderDialogProps = {
  order?: Order | null;
  errors?: string[];
  loading?: boolean;
  onClose: () => void;
  proceed: (isCloseWithRefund: boolean) => void;
};

const CancelOrderDialog: FC<CancelOrderDialogProps> = ({
  order,
  errors,
  loading,
  onClose,
  proceed,
}) => {
  const { t } = useTranslation(['checkout', 'common']);

  const [isCloseWithRefund, setIsCloseWithRefund] = useState(false);

  const isCloseWithRefundVisible =
    (order &&
      order.payments.some((payment) => payment.status === 'APPROVED')) ||
    false;

  return (
    <ModalNotification
      size='sm'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: t('checkout:cancelOrder'),
        content:
          errors && errors.length ? (
            <Stack sx={{ py: 1 }}>
              {errors.map((error, index) => (
                <Alert key={index} severity='error'>
                  {error}
                </Alert>
              ))}
            </Stack>
          ) : (
            <Stack sx={{ p: 1 }} spacing={2}>
              <Typography variant='body1'>
                {t('checkout:cancelOrderWarning')}
              </Typography>

              {isCloseWithRefundVisible && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={isCloseWithRefund}
                      onChange={(e) => setIsCloseWithRefund(e.target.checked)}
                    />
                  }
                  label='Close order with refund'
                />
              )}
            </Stack>
          ),
        actions: [
          {
            label: t('common:close'),
            onClick: onClose,
          },
          {
            label: t('common:proceed'),
            variant: 'contained',
            color: 'error',
            icon: loading && (
              <CircularProgress
                size='small'
                sx={{ color: '#fff', width: 16, height: 16, mr: 1 }}
              />
            ),
            onClick: () => {
              if (!loading) {
                return proceed(isCloseWithRefund);
              }
            },
          },
        ],
      }}
    />
  );
};

export default CancelOrderDialog;
